import useDegreeDetailV4 from "./rules/degreeDetailV4/useDegreeDetailV4";
import useExecEdATF from "./rules/execEdATF/useExecEdATF";
import useExecEdBTF from "./rules/execEdBTF/useExecEdBTF";
import useExecEdStickyCTA from "./rules/execEdStickyCTA/useExecEdStickyCTA";
import useExecEdLeadForm from "./rules/execEdLeadForm/useExecEdLeadForm";
import useExecEdDetailPageTest from "./rules/execEdDetailPageTest/useExecEdDetailPageTest";
import useHomepageOptimizationV3 from "./rules/homepageOptimizationV3/useHomepageOptimizationV3";
import useOcPromoBanner from "./rules/ocPromoBanner/useOcPromoBanner";
import useInternalTrackSelect from "./rules/trackSelection/useInternalTrackSelect";
import useRegisterCTA from "./rules/registerCTA/useRegisterCTA";
import useTrackSelectionRedesign from "./rules/trackSelectionRedesign/useTrackSelectionRedesign";
import useCytMobileFirstRedesign from "./rules/cytMobileFirstRedesign/useCytMobileFirstRedesign";
import useHubCardReorder from "./rules/hubCardReorder/useHubCardReorder";
import useProductCardRedesign from "./rules/productCardRedesign/useProductCardRedesign";
import useLogistrationTest from "./rules/logistrationTest/useLogistrationTest";
import useDegreeDetailRedesignTrafficV5_2 from "./rules/degreeDetailRedesignTrafficV5_2/useDegreeDetailRedesignTrafficV5_2";
import useProgramRedesignTraffic from "./rules/programRedesignTraffic/useProgramRedesignTraffic";
import useHeroStatsRedesign from "./rules/heroStatsRedesign/useHeroStatsRedesign";
import useLearnPageRedesign from "./rules/learnPageRedesign/useLearnPageRedesign";
import useTuitionModule from "./rules/tuitionModule/useTuitionModule";
import useProductSearch from "./rules/productSearch/useProductSearch";
import useSearchPagePromo from "./rules/searchPagePromo/useSearchPagePromo";
import useLogistrationPageRedesign from "./rules/logistrationPageRedesign/useLogistrationPageRedesign";
import useHomepageLOBEntrypointsTest from "./rules/homepageLOBEntrypoints/useHomepageLOBEntrypointsTest";
import useExecPromoHighlight from "./rules/execEdPromoHighlight/useExecPromoHighlight";
import useStreamlineCheckout from "./rules/streamlineCheckout/useStreamlineCheckout";
import useSingleCourseTraffic from "./rules/singleCourseTraffic/useSingleCourseTraffic";
import useStatsAndSocialProofTest from "./rules/statsAndSocialProofTest/useStatsAndSocialProofTest";
import useAbandonedCartIntentMatch from "./rules/abandonedCartIntentMatch/useAbandonedCartIntentMatch";
import useUpskillingSurveyPromotion from "./rules/upskillingSurveyPromotion/useUpskillingSurveyPromotion";
import useDegreeLeadFormContext from "./rules/degreeLeadFormContext/useDegreeLeadFormContext";
import useFindYourPath from "./rules/findYourPath/useFindYourPath";
import useResourcePageSideRail from "./rules/resourcePageSideRail/useResourcePageSideRail";
import useDynamicExperienceFlow from "./rules/dynamicExperienceFlow/useDynamicExperienceFlow";
import useDynamicExperienceSearch from "./rules/dynamicExperienceSearch/useDynamicExperienceSearch";
import useGTMSpotlightCarousel from "./rules/gtmSpotlightCarousel/useGTMSpotlightCarousel";
import useGetStartedWithModule from "./rules/getStartedWith/useGetStartedWithModule";
import useUserSurvey from "./rules/userSurvey/useUserSurvey";
import useUmbrellaPromoPage from "./rules/umbrellaPromoPage/useUmbrellaPromoPage";
import useSchoolRedesign from "./rules/schoolRedesign/useSchoolRedesign";
import useGuidedPathVsDynamicFlow from "./rules/guidedPathVsDynamicFlow/useGuidedPathVsDynamicFlow";
import useAuthxRecommendationsRedesign from "./rules/authxRecommendationsRedesign/useAuthxRecommendationsRedesign";
import useSuperlativesModule from "./rules/superlativesModule/useSuperlativesModule";
import useHomepageTrendingCourseUpdate from "./rules/homepageTrendingCourseUpdate/useHomepageTrendingCourseUpdate";
import useYourLearning from "./rules/yourLearning/useYourLearning";
import useCheckoutGeoPricing from "./rules/checkoutGeoPricing/useCheckoutGeoPricing";

// Define a common interface for experiment results
export interface MonarchTestResult<
  VariantType = string,
  DataType = Record<string, any>,
> {
  isLoading: boolean;
  variant?: VariantType;
  data?: DataType;
}

// Type for experiment hooks
export type MonarchTestHook<
  VariantType = string,
  DataType = Record<string, any>,
  PayloadType = unknown,
> = (payload?: PayloadType) => MonarchTestResult<VariantType, DataType>;

// Map of experiment rules
export const ruleMap: Record<string, MonarchTestHook<unknown, unknown, any>> = {
  useDegreeDetailV4,
  useHomepageOptimizationV3,
  useOcPromoBanner,
  useInternalTrackSelect,
  useExecEdATF,
  useExecEdBTF,
  useExecEdStickyCTA,
  useExecEdLeadForm,
  useExecEdDetailPageTest,
  useRegisterCTA,
  useTrackSelectionRedesign,
  useCytMobileFirstRedesign,
  useHubCardReorder,
  useProductCardRedesign,
  useLogistrationTest,
  useDegreeDetailRedesignTrafficV5_2,
  useProgramRedesignTraffic,
  useHeroStatsRedesign,
  useLearnPageRedesign,
  useTuitionModule,
  useProductSearch,
  useSearchPagePromo,
  useLogistrationPageRedesign,
  useHomepageLOBEntrypointsTest,
  useExecPromoHighlight,
  useStreamlineCheckout,
  useSingleCourseTraffic,
  useStatsAndSocialProofTest,
  useAbandonedCartIntentMatch,
  useUpskillingSurveyPromotion,
  useDegreeLeadFormContext,
  useFindYourPath,
  useResourcePageSideRail,
  useDynamicExperienceFlow,
  useDynamicExperienceSearch,
  useGTMSpotlightCarousel,
  useGetStartedWithModule,
  useUserSurvey,
  useUmbrellaPromoPage,
  useSchoolRedesign,
  useGuidedPathVsDynamicFlow,
  useAuthxRecommendationsRedesign,
  useSuperlativesModule,
  useHomepageTrendingCourseUpdate,
  useYourLearning,
  useCheckoutGeoPricing,
};

export function useMonarchTestResult<
  TestResult extends MonarchTestResult,
  PayloadType = unknown,
>(experimentKey: keyof typeof ruleMap, payload?: PayloadType): TestResult {
  const hook = ruleMap[experimentKey];

  if (!hook) {
    throw new Error(`MonarchTest "${experimentKey}" not found`);
  }

  const result = hook(payload) as TestResult;

  return {
    ...result,
    data: result.data || {},
  } as TestResult;
}
